import { DevString, memoFn, whenStr } from "@phosphor/utils";
import { useAtomValue } from "jotai";
import { Ta } from "#scripts/components/ui/icons.tsx";
import { isAtom } from "#scripts/lib/jotai/isAtom.ts";
import type { UID } from "#scripts/vmc/WorldState.ts";
import { World } from "#scripts/vmc/WorldState.ts";
import { getUIDColor } from "#scripts/world-state-demo/getUIDColor.tsx";
import { useDevHoverReference } from "#scripts/world-state-demo/useDevHover.ts";
import type { RegistryItem, StringRegistryItem } from "./DevState.tsx";
import { ProvideDevStateRenderers, defineRenderer, defineStringRenderer } from "./DevState.tsx";
import { DevStringLink } from "./DevStringLink.tsx";

const KEY_ORDER_PREFERENCE = ["devInfo", "name", "_hooks", "_source", "source", "parent"];

const uidElement = memoFn((uid: UID) => {
  const color = getUIDColor(uid);
  const style = {
    background: color.container.backgroundColor,
    color: color.textPrimary.color,
    borderRadius: "3px",
    padding: "2px 4px",
    "--uid-outline-width": "5px",
  };
  return () => {
    const devHoverProps = useDevHoverReference(uid);
    return (
      <b data-loc="phosphor-blog/scripts/lib/dev/app-dev-state-renderers.tsx:27:10" data-name="b" style={style} {...devHoverProps}>
        {uid}
      </b>
    );
  };
});

const RENDERERS: RegistryItem<any>[] = [
  defineRenderer(
    (thing) => isAtom(thing),
    ({ data, showPath, r }) => {
      const suggestedDebugLabel = showPath?.length ? showPath.join(".") : undefined;
      // if (!data.debugLabel && suggestedDebugLabel) data.debugLabel = suggestedDebugLabel;
      const customDebugLabel = data.debugLabel !== suggestedDebugLabel ? data.debugLabel : null;
      const value = useAtomValue(data);
      return <r.Unknown name={data.toString() + whenStr(customDebugLabel, `(#)`)} data={value} showPath={showPath} />;
    },
  ),
  defineRenderer(
    (thing) => thing instanceof DevString,
    ({ data, showPath, r }) => {
      const {
        context: { loc, ...context } = {},
        message,
      } = data.toJSON();
      const hasContext = Object.keys(context).length > 0;
      return (
        <r.Custom
          rawValue={data}
          icon={Ta.IconTestPipe}
          showPath={showPath}
          name="DevString"
          nameChild={<DevStringLink reason={data} />}
          collapse
        >
          {message.length > 1 || hasContext || data.cause ? (
            <div data-loc="phosphor-blog/scripts/lib/dev/app-dev-state-renderers.tsx:63:18" data-name="div" className="px-2 py-1 border border-neutral-200 rounded-md">
              {message.length > 1 && (
                <div data-loc="phosphor-blog/scripts/lib/dev/app-dev-state-renderers.tsx:65:22" data-name="div" className="flex flex-wrap gap-1">
                  {message.map((a, i) =>
                    typeof a === "string" ? (
                      <span data-loc="phosphor-blog/scripts/lib/dev/app-dev-state-renderers.tsx:68:29" data-name="span" className="text-xs font-semibold text-neutral-500" key={i + String(a)} children={a} />
                    ) : (
                      <r.Unknown name={String(i)} data={a} key={i + String(a)} showPath={["msg"]} />
                    ),
                  )}
                </div>
              )}
              {hasContext && <r.Unknown name="Context" data={context} showPath={["ctx"]} />}
              {data.cause && <r.Unknown name="Reason" data={data.cause} showPath={["because"]} />}
            </div>
          ) : null}
        </r.Custom>
      );
    },
  ),
];

const STRING_RENDERERS: StringRegistryItem<any>[] = [
  defineStringRenderer(
    (data) => (World.isUID(data) ? data : null),
    ({ data, r, showPath }) => {
      const Element = uidElement(data);
      return (
        <r.Custom rawValue={data} icon={Ta.IconBox} showPath={showPath} inline>
          <Element />
        </r.Custom>
      );
    },
  ),
];

export const ProvideAppDevStateRenderers = ({ children }: { children: React.ReactNode }) => {
  return (
    <ProvideDevStateRenderers registry={RENDERERS} stringRegistry={STRING_RENDERERS} orderedKeys={KEY_ORDER_PREFERENCE}>
      {children}
    </ProvideDevStateRenderers>
  );
};
